import React, { useEffect, useRef, useState } from "react";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import {
  Popover,
  Button,
  Tabs,
  Tab,
  Backdrop,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import MuiLink from "@mui/material/Link";
import { ScrollArea } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  ARCHIVE_ALL_NOTIFICATION,
  ARCHIVE_NOTIFICATION,
  DELETE_ALL_NOTIFICATIONS,
  DELETE_NOTIFICATION,
} from "../../../graphql/notification";
import { RenderNotificationRow } from "../../common/RenderNotificationRow";
import { toast } from "react-toastify";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import { useNotifications } from "../../../hooks/useNotifications";
import {
  DEFAULT_PAGINATION_SIZE,
  DEFAULT_PAGINATION_SCROLL_LIMIT,
} from "../../common/Constants";
import CardDetailsDrawer from "components/common/cards/CardDetailsDrawer";
import { GET_CARD_DETAIL } from "../../../graphql/card";
import { GET_TRANSACTION } from "../../../graphql/transaction";
import NotificationStatus from "../../../enums/NotificationStatus";
import {
  notificationTarget,
  surfaceForPath,
  type NotificationSurface,
  type NotificationTarget,
} from "utils/notifications/notificationTarget";
import ViewPaymentDetailsDrawer from "components/admin/payments/ViewPaymentDetailsDrawer";
import ContactDrawerRouter from "components/admin/contacts/ContactDrawerRouter";
import { ContactRouterDrawerType } from "enums/ContactRouterDrawerType";
import InvoiceDetailsDrawer from "components/admin/invoices/InvoiceDetailsDrawer";

/** The panel is a fixed-width sheet; its colour is the theme's. */
const panelSx = { width: 400 } as const;

const NOTIFICATION_SETTINGS_PATH: Record<NotificationSurface, string> = {
  admin: "/admin/user-notifications",
  employee: "/my/notifications",
  platform: "/platform/notifications",
};

interface NotificationPopoverProps {
  id?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  setAlmostTotalNotifications: (hasNotifications: number) => void;
}

const NotificationPopover: React.FC<NotificationPopoverProps> = (props) => {
  const { id, open, handleClose, anchorEl, setAlmostTotalNotifications } =
    props;

  const { t } = useTranslation();

  const [refetchIndex, setRefetchIndex] = useState(0);

  const [activeTab, setActiveTab] = useState(0);
  const notificationStatus =
    activeTab === 0 ? NotificationStatus.UNREAD : NotificationStatus.ARCHIVED;

  const surface = surfaceForPath(useLocation().pathname);
  // One record at a time: every drawer mounts off this, so two can never share an id.
  const recordDrawer = useDrawerRecord<NotificationTarget>();
  const openRecord = recordDrawer.record;
  const [opening, setOpening] = useState(false);
  const openingRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    formattedNotifications,
    isFetchingMore,
    variables,
    hasNextPage,
    endCursor,
    setIsFetchingMore,
    setVariables,
    loadingNotifications,
    total,
  } = useNotifications(notificationStatus, refetchIndex);

  const [getCardDetail] = useLazyQuery(GET_CARD_DETAIL, {
    fetchPolicy: "network-only",
  });

  const [getTransactionDetail] = useLazyQuery(GET_TRANSACTION, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (notificationStatus === NotificationStatus.UNREAD) {
      setAlmostTotalNotifications(total);
    }
  }, [total, notificationStatus, setAlmostTotalNotifications]);

  // Fetch the tab being opened, not the one being left, and drop any page still loading so it
  // cannot append the old tab's rows to the new one.
  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
    setIsFetchingMore(false);
    setVariables({
      status:
        newValue === 0
          ? NotificationStatus.UNREAD
          : NotificationStatus.ARCHIVED,
      first: DEFAULT_PAGINATION_SIZE,
    });
  };

  const handleRefreshNotifications = () => {
    setRefetchIndex(refetchIndex + 1); // this is the guarunteed change for refetch
    setVariables({
      first:
        formattedNotifications.length > DEFAULT_PAGINATION_SIZE
          ? formattedNotifications.length
          : DEFAULT_PAGINATION_SIZE,
      status: notificationStatus,
    });
  };

  // ScrollArea takes no onScroll, so this listens in the capture phase and
  // ignores every scroller but the list's (the Tabs have one too).
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = scrollRef.current;
    if (!target || e.target !== target) return;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const isNearBottom =
      (scrollTop + clientHeight) / scrollHeight >=
      DEFAULT_PAGINATION_SCROLL_LIMIT;

    if (isNearBottom && hasNextPage) {
      setIsFetchingMore(true);

      setVariables({
        ...variables,
        after: endCursor,
      });
    }
  };

  /** Looks the record up where the drawer needs it first, and returns what to open, or null. */
  const resolveRecord = async (
    target: NotificationTarget,
  ): Promise<NotificationTarget | null> => {
    switch (target.kind) {
      case "transaction": {
        const { data } = await getTransactionDetail({
          variables: { id: target.id },
        });
        if (!data?.transaction) {
          toast.error(t("Transaction deleted or does not exist"));
          return null;
        }
        return target;
      }
      case "card": {
        const { data } = await getCardDetail({ variables: { id: target.id } });
        if (!data?.card) {
          toast.error(t("Card deleted or does not exist"));
          return null;
        }
        return target;
      }
      default:
        return target;
    }
  };

  // A notification counts as read only once its record is actually on screen.
  const openNotification = async (
    target: NotificationTarget,
    notificationId: string,
  ) => {
    if (openingRef.current) return;
    openingRef.current = true;
    setOpening(true);
    try {
      const record = await resolveRecord(target);
      if (!record) return;
      recordDrawer.show(record);
      handleClose();
      if (notificationStatus === NotificationStatus.UNREAD) {
        archiveNotificationById({ variables: { id: notificationId } });
      }
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t("Error Please try again!"),
      );
    } finally {
      openingRef.current = false;
      setOpening(false);
    }
  };

  const handleCloseDrawer = recordDrawer.close;

  const [archiveNotificationById, { loading: archivingNotification }] =
    useMutation(ARCHIVE_NOTIFICATION, {
      variables: {
        id,
      },
      onCompleted: () => {
        toast.dismiss();
        toast.success(t("Notification marked as read"));
        handleRefreshNotifications();
      },
      onError: (error) => {
        toast.error(error?.message || t("Error Please try again!"));
      },
    });

  const [deleteNotificationById, { loading: deletingNotification }] =
    useMutation(DELETE_NOTIFICATION, {
      variables: {
        id,
      },
      onCompleted: () => {
        toast.dismiss();
        toast.success(t("Notification deleted"));
        handleRefreshNotifications();
      },
      onError: (error) => {
        toast.error(error?.message || t("Error Please try again!"));
      },
    });

  const [archiveAllNotifications, { loading: archivingAll }] = useMutation(
    ARCHIVE_ALL_NOTIFICATION,
    {
      onCompleted: () => {
        toast.dismiss();
        toast.success(t("Notifications marked as read"));
        handleRefreshNotifications();
      },
      onError: (error) => {
        toast.error(error?.message || t("Error Please try again!"));
      },
    },
  );

  const [deleteAllNotifications, { loading: deletingAll }] = useMutation(
    DELETE_ALL_NOTIFICATIONS,
    {
      onCompleted: () => {
        toast.dismiss();
        toast.success(t("Notifications deleted"));
        handleRefreshNotifications();
      },
      onError: (error) => {
        toast.error(error?.message || t("Error Please try again!"));
      },
    },
  );

  const loadingState =
    archivingNotification ||
    deletingNotification ||
    archivingAll ||
    deletingAll;

  return (
    <div style={{ position: "relative" }}>
      <Popover
        id={id}
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{ paper: { sx: panelSx } }}
      >
        <Stack
          gap="0"
          onScrollCapture={handleScroll}
          sx={{ maxHeight: "min(600px, calc(100vh - 110px))" }}
        >
          <Inline
            justify="between"
            wrap={false}
            sx={{
              paddingX: "var(--space-5)",
              paddingTop: "var(--space-5)",
              paddingBottom: "var(--space-3)",
            }}
          >
            <Typography variant="h6">{t("Notifications")}</Typography>
            {formattedNotifications?.length > 0 &&
              (notificationStatus === NotificationStatus.UNREAD ? (
                <Button
                  variant="default-outlined"
                  size="small"
                  onClick={() => {
                    archiveAllNotifications();
                  }}
                >
                  {t("Mark all as Read")}
                </Button>
              ) : (
                <Button
                  variant="default-outlined"
                  size="small"
                  onClick={() => {
                    deleteAllNotifications();
                  }}
                >
                  {t("Delete All")}
                </Button>
              ))}
          </Inline>
          <Tabs
            textColor="primary"
            indicatorColor="primary"
            value={activeTab}
            onChange={handleTabChange}
            aria-label={t("Notifications")}
            sx={{ paddingX: "var(--space-5)" }}
          >
            <Tab label={t("Unread")} sx={{ textTransform: "none" }} />
            <Tab label={t("Read")} sx={{ textTransform: "none" }} />
          </Tabs>
          <Divider />
          {/* The scroller is height 100% of a wrapper whose parent only has a
              max-height, which never resolves; nested flex lets it shrink instead. */}
          <ScrollArea
            contentRef={scrollRef}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
            contentSx={{ flex: 1, minHeight: 0, height: "auto" }}
          >
            {formattedNotifications?.length > 0 ? (
              <List disablePadding>
                {formattedNotifications.map((notification) => {
                  const target = notificationTarget(notification, surface);
                  return (
                    // The theme pads every ListItem regardless of disablePadding; the row pads itself.
                    <ListItem
                      key={notification.id}
                      divider
                      disablePadding
                      sx={{ padding: 0 }}
                    >
                      <RenderNotificationRow
                        onOpen={
                          target
                            ? () => openNotification(target, notification.id)
                            : undefined
                        }
                        notificationType={notification.type}
                        notificationBody={notification.body}
                        notificationActor={notification.actorName}
                        notificationTime={notification.daysFromToday}
                        notificationStatus={notification.status}
                        markNotificationAsRead={() => {
                          archiveNotificationById({
                            variables: {
                              id: notification.id,
                            },
                          });
                        }}
                        markNotificationAsDeleted={() => {
                          deleteNotificationById({
                            variables: {
                              id: notification.id,
                            },
                          });
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Stack
                gap="var(--space-3)"
                sx={{ alignItems: "center", padding: "var(--space-7)" }}
              >
                <Typography variant="body2">
                  {t("No notifications found")}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: "center" }}>
                  {t("To adjust your notification settings, go to")}{" "}
                  <MuiLink
                    component={Link}
                    to={NOTIFICATION_SETTINGS_PATH[surface]}
                    onClick={handleClose}
                  >
                    {t("your notification preferences")}
                  </MuiLink>
                  .
                </Typography>
              </Stack>
            )}
            {isFetchingMore && (
              <Typography
                variant="body2"
                role="status"
                sx={{ textAlign: "center", padding: "var(--space-5)" }}
              >
                {t("Loading more notifications")}
              </Typography>
            )}
          </ScrollArea>
        </Stack>
        <Backdrop
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          open={
            loadingState || opening || (loadingNotifications && !isFetchingMore)
          }
        >
          <CircularProgress color="primary" />
        </Backdrop>
      </Popover>
      {openRecord?.kind === "transaction" && (
        <TransactionDetailsDrawer
          open={recordDrawer.open}
          onAfterClose={recordDrawer.afterClose}
          handleCloseDrawer={handleCloseDrawer}
          transactionId={openRecord.id}
        />
      )}
      {openRecord?.kind === "card" && (
        <CardDetailsDrawer
          open={recordDrawer.open}
          onAfterClose={recordDrawer.afterClose}
          onClose={handleCloseDrawer}
          cardId={openRecord.id}
        />
      )}
      {openRecord?.kind === "invoice" && (
        <InvoiceDetailsDrawer
          open={recordDrawer.open}
          onAfterClose={recordDrawer.afterClose}
          invoiceId={openRecord.id}
          handleCloseInvoiceDetailsDrawer={handleCloseDrawer}
        />
      )}
      {openRecord?.kind === "payment" && (
        <ViewPaymentDetailsDrawer
          open={recordDrawer.open}
          onAfterClose={recordDrawer.afterClose}
          paymentId={openRecord.id}
          handleClose={handleCloseDrawer}
        />
      )}
      {openRecord?.kind === "contact" && (
        <ContactDrawerRouter
          open={recordDrawer.open}
          onAfterClose={recordDrawer.afterClose}
          drawerType={ContactRouterDrawerType.ExistingContact}
          contactId={openRecord.id}
          onClose={handleCloseDrawer}
        />
      )}
    </div>
  );
};

export default NotificationPopover;
